import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Plus, Send, Loader2, Trash2, Mail, Star, BellRing, PenLine, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls } from "@/lib/studioApi";

const ICONS = { envoi: Mail, relance: BellRing, avis: Star, perso: PenLine };
const ORDER = ["envoi", "relance", "avis", "perso"];

// Fenêtre d'envoi d'une facture : destinataires, copie, modèle (envoi / relance / avis / perso), objet et texte modifiables.
export const InvoiceSendModal = ({ invoiceId, reference, defaultTemplate = "envoi", onClose, onSent }) => {
  const [data, setData] = useState(null);
  const [to, setTo] = useState([]);
  const [ccMe, setCcMe] = useState(false);
  const [template, setTemplate] = useState(defaultTemplate);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    studio.get(`/invoices/${invoiceId}/email-templates`).then(({ data: d }) => {
      setData(d);
      setTo(d.recipients.length ? d.recipients : [""]);
      const t = d.templates[defaultTemplate] || d.templates.envoi;
      setSubject(t.subject);
      setBody(t.body);
    }).catch((e) => { toast.error(apiError(e, "Chargement impossible.")); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, defaultTemplate]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pick = (key) => {
    setTemplate(key);
    const t = data.templates[key];
    setSubject(t.subject);
    setBody(t.body);
  };

  const send = async () => {
    const recipients = to.map((e) => e.trim()).filter((e) => e.includes("@"));
    if (!recipients.length) return toast.error("Indiquez au moins un destinataire valide.");
    setBusy(true);
    try {
      const { data: r } = await studio.post(`/invoices/${invoiceId}/send`, { template, to: recipients, cc_me: ccMe, subject, body });
      toast.success(`${data.templates[template].label} — email envoyé à ${r.client_email}.${r.failed?.length ? ` Échec pour : ${r.failed.join(", ")}` : ""}`);
      onSent?.(r);
      onClose();
    } catch (e) { toast.error(apiError(e, "Envoi impossible.")); }
    finally { setBusy(false); }
  };

  const current = data?.templates[template];

  return (
    <div data-testid="invoice-send-modal" className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[#0F2645]/55 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-[0_30px_80px_-30px_rgba(8,16,77,0.6)] sm:rounded-3xl"
        style={{ animation: "devSlideUp .22s ease-out" }}
      >
        <div className="flex items-center justify-between gap-3 border-b border-[#E2E7F7] px-5 py-4 sm:px-6">
          <h2 className="font-heading text-base font-extrabold leading-snug tracking-tight text-[#0F2645] sm:text-lg">
            Envoyer la facture <span className="font-mono whitespace-nowrap">n° {reference}</span>
          </h2>
          <button type="button" data-testid="send-modal-close" onClick={onClose} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[#93A0BC] hover:bg-slate-100">
            <X className="h-4.5 w-4.5" />
          </button>
        </div>

        {!data ? (
          <div className="flex items-center justify-center gap-2 px-6 py-16 text-sm text-[#5C6A87]"><Loader2 className="h-4 w-4 animate-spin" /> Préparation de l'email…</div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            {/* Destinataires */}
            <p className="text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Destinataires</p>
            <div className="mt-2 space-y-2">
              {to.map((email, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    data-testid={`send-recipient-${i}`}
                    type="email"
                    className={inputCls}
                    placeholder="adresse@email.be"
                    value={email}
                    onChange={(e) => setTo(to.map((x, idx) => (idx === i ? e.target.value : x)))}
                  />
                  {to.length > 1 && (
                    <button type="button" data-testid={`send-recipient-remove-${i}`} onClick={() => setTo(to.filter((_, idx) => idx !== i))} className="shrink-0 text-[#93A0BC] hover:text-rose-600" aria-label="Retirer">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" data-testid="send-add-recipient" onClick={() => setTo([...to, ""])} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3A5CC8] hover:underline">
              <Plus className="h-4 w-4" /> Ajouter un destinataire
            </button>

            {/* Copie */}
            <label className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] px-4 py-3">
              <span className="text-sm font-semibold text-[#0F2645]">Recevoir une copie <span className="block text-xs font-normal text-[#5C6A87]">{data.owner_email}</span></span>
              <button
                type="button"
                role="switch"
                aria-checked={ccMe}
                data-testid="send-cc-toggle"
                onClick={() => setCcMe(!ccMe)}
                className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${ccMe ? "bg-[#3A5CC8]" : "bg-slate-300"}`}
              >
                <span className={`absolute left-0 top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${ccMe ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </label>

            {/* Modèle */}
            <p className="mt-5 text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Modèle</p>
            <div className="mt-2 flex gap-1 overflow-x-auto rounded-xl bg-[#F3F5FC] p-1">
              {ORDER.map((key) => {
                const Icon = ICONS[key];
                const active = template === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-testid={`send-template-${key}`}
                    onClick={() => pick(key)}
                    className={`inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-semibold transition-all ${
                      active ? "bg-white text-[#3A5CC8] shadow-sm" : "text-[#5C6A87] hover:text-[#0F2645]"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {data.templates[key].label}
                  </button>
                );
              })}
            </div>

            {current?.needs_review_url && (
              <p data-testid="send-review-url-missing" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-900">
                Ajoutez d'abord votre lien d'avis (Google, etc.) dans{" "}
                <Link to="/studio/profil" className="font-bold underline">votre profil</Link> : le bouton « Laisser un avis » y renverra.
              </p>
            )}

            {/* Objet + corps */}
            <div className="mt-4 rounded-2xl border border-[#E2E7F7] bg-[#F6F8FD]/70 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#93A0BC]">Objet de l'email</p>
              <input data-testid="send-subject" className="mt-1 w-full bg-transparent font-heading text-base font-bold text-[#0F2645] outline-none sm:text-lg" value={subject} onChange={(e) => setSubject(e.target.value)} />
              <textarea
                data-testid="send-body"
                rows={9}
                className="mt-3 w-full resize-y rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 text-sm leading-relaxed text-[#0F2645] outline-none focus:border-[#3A5CC8] focus:ring-2 focus:ring-[#3A5CC8]/20"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <div className="mt-3 flex justify-center">
                <span data-testid="send-button-preview" className="inline-flex items-center gap-2 rounded-xl bg-[#3A5CC8]/80 px-6 py-3 text-sm font-bold text-white">
                  {current?.button} <ExternalLink className="h-3.5 w-3.5" />
                </span>
              </div>
              <p className="mt-2 text-center text-[11px] text-[#93A0BC]">Ce bouton est ajouté automatiquement sous votre texte, avec votre logo en en-tête.</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-[#E2E7F7] bg-white px-5 py-3.5 sm:px-6">
          <button type="button" data-testid="send-cancel" onClick={onClose} className="inline-flex min-h-[44px] items-center rounded-xl border border-[#E2E7F7] px-5 text-sm font-semibold text-[#5C6A87] hover:border-[#E2E7F7]">Annuler</button>
          <button
            type="button"
            data-testid="send-confirm"
            disabled={busy || !data || current?.needs_review_url}
            onClick={send}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#3A5CC8] px-6 text-sm font-bold text-white shadow-[0_10px_24px_-12px_rgba(58,92,200,0.9)] transition-all hover:-translate-y-0.5 hover:bg-[#3B5ECB] disabled:opacity-40"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer
          </button>
        </div>
      </div>
    </div>
  );
};
